import { ClerkAxiosSetup } from '@/api/ClerkAxiosSetup';
import { Toaster } from '@/components/design-system/sooner';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { clerkConfig } from '@/config/clerk.config';
import { env } from '@/env/env';
import { useMockServiceWorker } from '@/hooks/useMockServiceWorker';
import { LocaleStoreSyncProvider } from '@/i18n/LocaleStoreSyncProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { FullStoryProvider } from '@/providers/FullStoryProvider';
import { IntercomProvider } from '@/providers/IntercomProvider';
import { MatchMediaContextProvider } from '@/providers/MatchMediaProvider';
import { MockAuthProvider } from '@/providers/MockAuthProvider';
import { OrganizationProvider } from '@/providers/OrganizationProvider';
import { router } from '@/routing/router';
import { ClerkProvider } from '@clerk/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router/dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';

export const queryClient = new QueryClient();

const SharedProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <MatchMediaContextProvider>
    {children}
    <Toaster richColors />
    <ReactTooltip
      id="app-tooltip"
      delayShow={300}
      border="1px solid #e5e5e5"
      opacity={1}
      style={{
        backgroundColor: 'white',
        color: '#1a1a1a',
        borderRadius: '6px',
        padding: '4px 8px',
        fontSize: '12px',
        zIndex: 9999,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        maxWidth: '400px'
      }}
    />
  </MatchMediaContextProvider>
);

export const Providers: React.FC = () => {
  const { isReady } = useMockServiceWorker();

  if (!isReady) {
    return null;
  }

  if (env.VITE_MOCK_MODE) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <MockAuthProvider>
            <SharedProviders>
              <RouterProvider router={router} />
            </SharedProviders>
          </MockAuthProvider>
          <LocaleStoreSyncProvider />
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ClerkProvider {...clerkConfig}>
        <OrganizationProvider>
          <ClerkAxiosSetup>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <FullStoryProvider>
                  <IntercomProvider>
                    <SharedProviders>
                      <RouterProvider router={router} />
                    </SharedProviders>
                  </IntercomProvider>
                </FullStoryProvider>
              </AuthProvider>
              <LocaleStoreSyncProvider />
            </QueryClientProvider>
          </ClerkAxiosSetup>
        </OrganizationProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
};
